import React from 'react';
import styled from 'styled-components';
import {useOutlet, useOutletSetter} from 'reconnect.js';
import {Badge, Button} from 'antd';
import useBreakpoint from '../../Hooks/useBreakPoint';
import Link from '../NavLink';
import * as AppActions from '../../AppActions';
import {ShoppingBag} from '@styled-icons/boxicons-regular/ShoppingBag';
import {PersonCircle} from '@styled-icons/ionicons-outline/PersonCircle';

const navItems = [
  {children: '關於Pinbar', url: '/about'},
  {children: '商品項目', url: '/products'},
  {children: '部落格', url: '/articles'},
];

function _isInPath({location = {}, navItemUrl}) {
  if (navItemUrl === '/articles?cat=news') {
    if (location.pathname === '/articles' && location.search === '?cat=news') {
      // workaround for special case '/articles?cat=news'
      return true;
    } else {
      return false;
    }
  } else {
    if (location.search === '?cat=news') {
      return false;
    }
    return location.pathname?.indexOf(navItemUrl) === 0;
  }
}

function SiteNavBar(props) {
  const {location} = props;
  const [user] = useOutlet('user');
  const [cart] = useOutlet('cart');
  const [dimension] = useOutlet('dimension');
  const showLoginModal = useOutletSetter('login-modal');
  const {passBreakpoint} = useBreakpoint(100);
  const [mobileNavVisible, setMobileNavVisible] = React.useState(false);
  const mobile = dimension.rwd !== 'desktop';
  const pathname = location?.pathname;
  React.useEffect(() => {
    setMobileNavVisible(false);
  }, [pathname]);

  return (
    <>
      <NavBar hasBorder={passBreakpoint} bgColor={'white'} style={{height: 64}}>
        <Logo style={{cursor: 'pointer'}}>
          <Link to="/" loading={true}>
            <img
              src="/images/pinbar-logo-horizontal.png"
              alt="Logo"
              style={{
                height: dimension.rwd === 'desktop' ? 30 : 20,
                objectFit: 'contain',
              }}
            />
          </Link>
        </Logo>

        {mobile && (
          <>
            <div style={{flex: 1}} />
            <img
              onClick={() => setMobileNavVisible(true)}
              src="/images/menu.png"
              width="25"
              height="25"
            />
          </>
        )}

        {!mobile && (
          <>
            <div style={{display: 'flex', flex: 1, justifyContent: 'center'}}>
              {navItems.map(({children, url}, idx) => {
                let selected = _isInPath({
                  location,
                  navItemUrl: url,
                });

                let loading = true;
                if (url === '/about') {
                  loading = 800;
                }

                return (
                  <Link
                    to={url}
                    loading={loading}
                    className="nav-link"
                    key={idx}
                    extraStyle={{
                      color: '#000',
                      fontWeight: '400',
                      borderBottom: selected
                        ? '4px solid #0eb407'
                        : '4px solid transparent',
                      padding: '19px 10px',
                      margin: '0px 20px',
                    }}>
                    {children}
                  </Link>
                );
              })}
            </div>

            <Button
              type="link"
              onClick={() => {
                AppActions.navigate('/checkout', {loading: 800});
              }}>
              <Badge count={cart.items.length}>
                <img
                  src="/images/cart.png"
                  width="25"
                  height="25"
                  style={{cursor: 'pointer'}}
                />
              </Badge>
            </Button>

            {user ? (
              <div
                onClick={() => AppActions.navigate('/profile', {loading: 800})}
                style={{cursor: 'pointer'}}>
                <img src="/images/user.png" width="25" height="25" />
              </div>
            ) : (
              <div
                onClick={() => showLoginModal(true)}
                style={{cursor: 'pointer'}}>
                <img src="/images/user.png" width="25" height="25" />
              </div>
            )}
          </>
        )}
      </NavBar>

      <MobileNav
        visible={mobile && mobileNavVisible}
        setVisible={setMobileNavVisible}
        location={location}
      />
    </>
  );
}

const NavBar = styled.div`
  position: fixed;
  background-color: ${(props) => props.bgColor};
  top: 0px;
  left: 0px;
  width: 100vw;
  height: var(--topNavBarHeight);
  padding: 16px 40px;
  display: flex;
  align-items: center;
  z-index: 10;
  /* box-shadow: ${(props) =>
    props.hasBorder
      ? '0 10px 20px rgba(0, 0, 0, 0.19), 0 6px 6px rgba(0, 0, 0, 0.23)'
      : 'none'}; */
  transition: 200ms;
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
`;

function MobileNav(props) {
  const [user] = useOutlet('user');
  const [cart] = useOutlet('cart');
  const showLoginModal = useOutletSetter('login-modal');
  const {visible, setVisible, location} = props;
  return (
    <>
      <Backdrop visible={visible} onClick={() => setVisible(false)} />
      <MobileContent visible={visible}>
        <Logo
          style={{cursor: 'pointer', alignSelf: 'center', marginBottom: 20}}>
          <Link to="/">
            <img
              src="/images/pinbar-logo.png"
              alt="site logo"
              style={{width: 150}}
            />
          </Link>
        </Logo>
        {navItems.map(({children, url}, idx) => {
          let selected = _isInPath({
            location,
            navItemUrl: url,
          });

          let loading = true;
          if (url === '/about') {
            loading = 800;
          }

          return (
            <Link
              to={url}
              loading={loading}
              className="nav-link"
              key={idx}
              extraStyle={{
                color: selected ? '#0eb407' : '#000',
                fontWeight: '400',
                padding: '19px 10px',
                textAlign: 'center',
              }}>
              {children}
            </Link>
          );
        })}

        <Link
          to={'/checkout'}
          loading={800}
          className="nav-link"
          key="checkout"
          extraStyle={{
            color: _isInPath({
              location,
              navItemUrl: '/checkout',
            })
              ? '#0eb407'
              : '#000',
            fontWeight: '400',
            padding: '19px 10px',
            textAlign: 'center',
          }}>
          <Badge count={cart.items.length}>
            <img
              src="/images/cart.png"
              width="25"
              height="25"
              style={{cursor: 'pointer'}}
            />
          </Badge>
        </Link>

        {user ? (
          <div
            onClick={() => AppActions.navigate('/profile')}
            style={{
              cursor: 'pointer',
              width: '100%',
              textAlign: 'center',
              padding: '19px 10px',
            }}>
            <img src="/images/user.png" width="25" height="25" />
          </div>
        ) : (
          <div
            onClick={() => showLoginModal(true)}
            style={{
              cursor: 'pointer',
              width: '100%',
              textAlign: 'center',
              padding: '19px 10px',
            }}>
            <img src="/images/user.png" width="25" height="25" />
          </div>
        )}
      </MobileContent>
    </>
  );
}

const Backdrop = styled.div`
  z-index: 100;
  position: fixed;
  left: 0;
  top: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.3);
  opacity: ${(props) => (props.visible ? 1 : 0)};
  ${(props) =>
    !props.visible &&
    `
    pointer-events: none;
  `}
  transition: 300ms;
`;

const MobileContent = styled.div`
  z-index: 101;
  position: fixed;
  left: 0;
  top: 0;
  width: 240px;
  height: 100vh;
  opacity: ${(props) => (props.visible ? 1 : 0)};
  transform: ${(props) =>
    props.visible ? 'translateX(0px)' : 'translateX(-300px)'};
  ${(props) =>
    !props.visible &&
    `
    pointer-events: none;
  `}
  transition: 300ms;

  padding: 20px;
  background-color: white;
  display: flex;
  flex-direction: column;
`;

export default SiteNavBar;
